import { createTheme } from '@mui/material/styles';
import { TypeBackground, TypeText } from '@mui/material/styles/createPalette';

// HAVEN Official Brand Colors (authentic brand palette)
const havenColors = {
  // Primary brand colors - OFFICIAL HAVEN PALETTE
  primaryTeal: '#00C4C1',    // "Blewstream" - HAVEN's signature teal
  primaryYellow: '#FFE623',  // "Yellowflow" - HAVEN's signature yellow
  
  // IAQ Status colors from pro portal
  iaqGood: '#58eac7',
  iaqFair: '#e5ba00', 
  iaqPoor: '#f36262',
  
  // Supporting colors
  darkTeal: '#008B8A',
  lightTeal: '#33D4D1',
  darkGray: '#2C2C2C',
  secondaryGrey: '#535F66', // Navigation and secondary text color
  lightGray: '#F5F5F5',
  white: '#FFFFFF',
};

// Custom theme extending MUI defaults
export const havenTheme = createTheme({
  palette: {
    primary: {
      main: havenColors.primaryTeal,
      dark: havenColors.darkTeal,
      light: havenColors.lightTeal,
      contrastText: havenColors.white,
    },
    secondary: {
      main: havenColors.primaryYellow,
      dark: '#E6CF00',
      light: '#FFF04D',
      contrastText: havenColors.darkGray,
    },
    error: {
      main: havenColors.iaqPoor,
    },
    warning: {
      main: havenColors.iaqFair,
    },
    success: {
      main: havenColors.iaqGood,
    },
    background: {
      default: havenColors.white,
      paper: havenColors.white,
    } as TypeBackground,
    text: {
      primary: havenColors.darkGray,
      secondary: '#666666',
    } as TypeText,
    grey: {
      50: '#F9F9F9',
      100: havenColors.lightGray,
      200: '#E0E0E0',
      300: '#C4C4C4',
      400: '#A8A8A8',
      500: '#8C8C8C',
      600: '#707070',
      700: '#545454',
      800: '#383838',
      900: havenColors.darkGray,
    },
  },
  typography: {
    fontFamily: 'Rubik, "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    // Custom component styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          fontWeight: 500,
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${havenColors.primaryTeal} 0%, ${havenColors.lightTeal} 100%)`,
          boxShadow: '0 4px 14px 0 rgba(0, 196, 193, 0.39)',
          '&:hover': {
            background: `linear-gradient(135deg, ${havenColors.darkTeal} 0%, ${havenColors.primaryTeal} 100%)`,
            boxShadow: '0 6px 20px 0 rgba(0, 196, 193, 0.5)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${havenColors.primaryYellow} 0%, #FFF04D 100%)`,
          color: havenColors.darkGray,
          '&:hover': {
            background: `linear-gradient(135deg, #E6CF00 0%, ${havenColors.primaryYellow} 100%)`,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'white', // Updated to white as per layout requirements
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #F0F0F0',
        },
      },
    },
  },
});

// Export colors for use in components
export { havenColors }; 